import * as React from 'react';
import Stats from "../components/Stats";
import {IocStore} from "../stores/ioc";
import {observer, useLocalObservable} from "mobx-react-lite";
import LogsStore from "../stores/logs";
import {Badge, Button, Col, Form} from "react-bootstrap";
import {useHistory} from "react-router-dom";
import {ChangeEvent, FormEvent} from "react";
import {HotIocStore, WorstCountryStore, WorstIpStore} from "../stores/stats";
import {refangIoc, IOC_PATTERN} from "../utils/ioc";

interface Props {
    iocStore: IocStore
    logsStore: LogsStore
    worstIpStore: WorstIpStore
    worstCountryStore: WorstCountryStore
    hotIocStore: HotIocStore
}

const HomePage = observer(function HomePage(props: Props) {
    const history = useHistory()

    const localState = useLocalObservable((): {validated: boolean, ioc: string, file?: File, invalidFile: boolean, iocInvalid: boolean} => ({
        validated: false,
        ioc: '',
        file: undefined,
        invalidFile: false,
        iocInvalid: false
    }))

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        localState.validated = true

        if (localState.ioc) {
            const refanged = refangIoc(localState.ioc)
            if (!IOC_PATTERN.test(refanged)) {
                localState.iocInvalid = true
                return false
            }
            props.iocStore.setIoc(refanged)
            history.push('/auth', {check: 'ioc'})
        } else if (localState.file) {
            props.logsStore.setFile(localState.file)
            history.push('/auth', {check: 'log'})
        } else {
            return false
        }
    }

    return <>
        <p>Check IOC is a free tool for the community to lookup IP addresses and domains against our extensive database of malware-related IOCs.</p>
        <p>This free version allows 25 queries per day. You can also <a href="https://www.threatstop.com/check-ioc-subscription" target="_blank" rel="noreferrer">sign up</a> for a free trial of our product which provides access to unlimited searches with extended meta data such as passive DNS.</p>

        <Form noValidate validated={localState.validated} onSubmit={handleSubmit} className="homepage-form">
            <Form.Row>
                <Form.Group as={Col}>
                    <Form.Label htmlFor="check_ioc">Enter the IP address or Domain to find out what we know</Form.Label>
                    <Form.Control
                        id="check_ioc"
                        name="check_ioc"
                        onChange={(e) => { localState.ioc = e.target.value; localState.iocInvalid = false }}
                        placeholder="bad.threatstop.com"
                        aria-describedby="iocHelpBlock"
                        isInvalid={(localState.validated && !localState.ioc && !localState.file) || localState.iocInvalid}
                    />
                    <Form.Control.Feedback type="invalid">
                        Invalid IOC. IOC can be a (wildcard) domain, IP address or /32 CIDR block.
                    </Form.Control.Feedback>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col}>
                    <Badge variant="dark">OR</Badge>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col}>
                    <Form.Label htmlFor="check_logs">Upload a log (text format)</Form.Label>
                    <Form.File id="check_logs" custom>
                        <Form.File.Input
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                localState.invalidFile = false

                                if (e.target.files) {
                                    const file = e.target.files[0]

                                    if (file && file.size <= 32768) {
                                        localState.file = file
                                    } else {
                                        localState.invalidFile = true
                                        localState.file = undefined
                                    }
                                } else {
                                    localState.file = undefined
                                }
                            }}
                            isInvalid={localState.invalidFile}
                        />
                        <Form.File.Label data-browse="Select">
                            {localState.file ? localState.file.name : 'Select a log file'}
                        </Form.File.Label>
                    </Form.File>
                    <Form.Text className="text-muted">
                        The CheckIOC tool will scan the logs (up to 32Kb) for IPs and domain names and report on the first 10.
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                        Invalid log file
                    </Form.Control.Feedback>
                </Form.Group>
            </Form.Row>
            <Form.Row>
                <Form.Group as={Col}>
                    <Button type="submit">Check IOC</Button>
                </Form.Group>
            </Form.Row>
        </Form>
        <Stats worstIpStore={props.worstIpStore} worstCountryStore={props.worstCountryStore} hotIocStore={props.hotIocStore} />
    </>
})

export default HomePage